<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useSelectionStore } from '../../stores/selectionStore'
import { useVehicleStore } from '../../stores/vehicleStore'
import { useSearchHistoryStore } from '../../stores/searchHistoryStore'

const selectionStore = useSelectionStore()
const vehicleStore = useVehicleStore()
const historyStore = useSearchHistoryStore()

onMounted(() => {
  if (selectionStore.manufacturers.length === 0) {
    selectionStore.loadManufacturers()
  }
})

async function onManufacturerChange(e: Event) {
  const val = Number((e.target as HTMLSelectElement).value)
  if (val > 0) {
    await selectionStore.selectManufacturer(val)
  }
}

async function onModelChange(e: Event) {
  const val = Number((e.target as HTMLSelectElement).value)
  if (val > 0) {
    await selectionStore.selectModel(val)
  }
}

async function onMotorChange(e: Event) {
  const val = Number((e.target as HTMLSelectElement).value)
  if (val > 0) {
    selectionStore.selectMotor(val)
    await vehicleStore.selectMotor(val)

    if (vehicleStore.carDetails && !vehicleStore.searchError) {
      const d = vehicleStore.carDetails
      const manu = selectionStore.manufacturers.find(m => m.manuId === selectionStore.selectedManufacturerId)
      historyStore.addEntry({
        type: 'manual',
        query: `${manu?.manuName || ''} ${d.modelName} ${d.typeName || ''}`.trim(),
        carName: `${d.manuName} ${d.modelName} ${d.typeName || ''}`.trim(),
        carId: val,
      })
    }
  }
}

function formatYear(raw: number): string {
  if (!raw || raw === 0) return ''
  return String(Math.floor(raw / 100))
}
</script>

<template>
  <div class="manual-select">
    <div class="manual-select__step">
      <label>1. Velg merke</label>
      <div class="manual-select__dropdown">
        <select
          :value="selectionStore.selectedManufacturerId ?? 0"
          @change="onManufacturerChange"
          :disabled="selectionStore.isLoadingManufacturers"
        >
          <option :value="0">Velg</option>
          <option
            v-for="m in selectionStore.manufacturers"
            :key="m.manuId"
            :value="m.manuId"
          >
            {{ m.manuName }}
          </option>
        </select>
        <span v-if="selectionStore.isLoadingManufacturers" class="spinner-sm"></span>
      </div>
    </div>

    <div class="manual-select__step">
      <label>2. Velg modell</label>
      <div class="manual-select__dropdown">
        <select
          :value="selectionStore.selectedModelId ?? 0"
          @change="onModelChange"
          :disabled="!selectionStore.selectedManufacturerId || selectionStore.isLoadingModels"
        >
          <option :value="0">Velg</option>
          <option
            v-for="m in selectionStore.models"
            :key="m.modelId"
            :value="m.modelId"
          >
            {{ m.modelName }}
            <template v-if="m.yearFrom">
              ({{ formatYear(m.yearFrom) }}<template v-if="m.yearTo"> - {{ formatYear(m.yearTo) }}</template>)
            </template>
          </option>
        </select>
        <span v-if="selectionStore.isLoadingModels" class="spinner-sm"></span>
      </div>
    </div>

    <div class="manual-select__step">
      <label>3. Velg motor</label>
      <div class="manual-select__dropdown">
        <select
          :value="selectionStore.selectedMotorId ?? 0"
          @change="onMotorChange"
          :disabled="!selectionStore.selectedModelId || selectionStore.isLoadingMotors"
        >
          <option :value="0">Velg</option>
          <option
            v-for="m in selectionStore.motors"
            :key="m.carId"
            :value="m.carId"
          >
            {{ m.description }} {{ m.hp }}HK {{ m.engineCode }}
            <template v-if="m.yearFrom">
              ({{ formatYear(m.yearFrom) }}<template v-if="m.yearTo"> - {{ formatYear(m.yearTo) }}</template>)
            </template>
          </option>
        </select>
        <span v-if="selectionStore.isLoadingMotors" class="spinner-sm"></span>
      </div>
    </div>

    <div v-if="selectionStore.error" class="manual-select__error">
      {{ selectionStore.error }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.manual-select {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;

  &__step {
    flex: 1;
    min-width: 180px;

    label {
      display: block;
      font-weight: 600;
      margin-bottom: 0.4rem;
    }
  }

  &__dropdown {
    position: relative;

    select {
      width: 100%;
      padding: 10px 12px;
      border: 2px solid #ddd;
      border-radius: 6px;
      font-size: 0.95rem;
      appearance: none;
      background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &:focus {
        border-color: #002868;
        outline: none;
      }
    }
  }

  &__error {
    width: 100%;
    padding: 0.5rem;
    background: #fef2f2;
    color: #991b1b;
    border-radius: 6px;
    font-size: 0.85rem;
  }
}

.spinner-sm {
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  border: 2px solid #ddd;
  border-top-color: #002868;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
